/**
 * EditPosContagemModal - Modal para editar pós-contagem por cliente
 *
 * Epic Redesign Pós-Contagem:
 * - Edição de dados de pós-contagem por cliente
 * - Campos: locais, lacres, O.C., datas, observação, status
 */
import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, MapPin, Tag, FileText, Clock, Send, MessageSquare, CheckCircle } from 'lucide-react';
import { PosContagemCliente, PosContagemClienteInput } from '../../services/contagemService';

interface EditPosContagemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<PosContagemClienteInput>) => Promise<void>;
  item: PosContagemCliente | null;
  isSaving: boolean;
}

// Status options
const STATUS_OPTIONS = [
  { value: 'PENDENTE', label: 'Pendente', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  { value: 'EM_ANDAMENTO', label: 'Em Andamento', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'CONCLUIDO', label: 'Concluído', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'CANCELADO', label: 'Cancelado', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
];

export const EditPosContagemModal: React.FC<EditPosContagemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  item,
  isSaving,
}) => {
  const [formData, setFormData] = useState({
    locais_contados: '',
    lacres: '',
    ordem_coleta: '',
    data_hora_inicio: '',
    data_hora_termino: '',
    data_envio_relatorio: '',
    observacao: '',
    status: 'PENDENTE' as string,
  });

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        locais_contados: item.locais_contados || '',
        lacres: item.lacres || '',
        ordem_coleta: item.ordem_coleta || '',
        data_hora_inicio: item.data_hora_inicio || '',
        data_hora_termino: item.data_hora_termino || '',
        data_envio_relatorio: item.data_envio_relatorio || '',
        observacao: item.observacao || '',
        status: item.status || 'PENDENTE',
      });
    }
  }, [item]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  if (!isOpen || !item) return null;

  const inputClass = 'w-full px-3 py-2 text-sm border-2 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-600 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 dark:focus:ring-emerald-800 transition-all';
  const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">
              Editar Pós-Contagem
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {item.cliente_nome}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Locais Contados */}
          <div>
            <label className={labelClass}>
              <MapPin size={12} className="inline mr-1" />
              Locais Contados
            </label>
            <input
              type="text"
              value={formData.locais_contados}
              onChange={(e) => handleChange('locais_contados', e.target.value)}
              placeholder="Ex: Farmácia, Almoxarifado, Centro Cirúrgico"
              className={inputClass}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">
              Separe múltiplos locais por vírgula
            </p>
          </div>

          {/* Grid: Lacres e O.C. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                <Tag size={12} className="inline mr-1" />
                Lacres
              </label>
              <input
                type="text"
                value={formData.lacres}
                onChange={(e) => handleChange('lacres', e.target.value)}
                placeholder="Ex: LAC-123, LAC-456"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                <FileText size={12} className="inline mr-1" />
                O.C. (Ordem de Coleta)
              </label>
              <input
                type="text"
                value={formData.ordem_coleta}
                onChange={(e) => handleChange('ordem_coleta', e.target.value)}
                placeholder="Ex: OC-534-001"
                className={inputClass}
              />
            </div>
          </div>

          {/* Grid: Datas */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                <Clock size={12} className="inline mr-1" />
                Início Contagem
              </label>
              <input
                type="text"
                value={formData.data_hora_inicio}
                onChange={(e) => handleChange('data_hora_inicio', e.target.value)}
                placeholder="DD/MM/YYYY HH:MM"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                <Clock size={12} className="inline mr-1" />
                Término Contagem
              </label>
              <input
                type="text"
                value={formData.data_hora_termino}
                onChange={(e) => handleChange('data_hora_termino', e.target.value)}
                placeholder="DD/MM/YYYY HH:MM"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                <Send size={12} className="inline mr-1" />
                Envio Relatório
              </label>
              <input
                type="text"
                value={formData.data_envio_relatorio}
                onChange={(e) => handleChange('data_envio_relatorio', e.target.value)}
                placeholder="DD/MM/YYYY HH:MM"
                className={inputClass}
              />
            </div>
          </div>

          {/* Observação */}
          <div>
            <label className={labelClass}>
              <MessageSquare size={12} className="inline mr-1" />
              Observação
            </label>
            <textarea
              value={formData.observacao}
              onChange={(e) => handleChange('observacao', e.target.value)}
              placeholder="Observações gerais sobre a pós-contagem deste cliente..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          {/* Status */}
          <div>
            <label className={labelClass}>
              <CheckCircle size={12} className="inline mr-1" />
              Status
            </label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('status', option.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border-2 ${
                    formData.status === option.value
                      ? `${option.color} border-emerald-500 dark:border-emerald-400 ring-2 ring-emerald-200 dark:ring-emerald-800`
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-transparent hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition font-medium disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
